import { Component, OnInit, Input } from '@angular/core';
import { UserService } from 'src/app/services/shared/user.service';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { finalize, distinctUntilChanged } from 'rxjs/operators';

import { AgentDetailsResponse } from '../model/agentDetailsResponse.model';
import { VerifyPhoneReqRes } from '../model/VerifyPhoneReqRes.model';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { DialogService } from 'src/app/services/dialog.service';
import { ConfirmDialogParams } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'terra-two-factor-authentication',
  templateUrl: './two-factor-authentication.component.html',
  styleUrls: ['./two-factor-authentication.component.scss']
})
export class TwoFactorAuthenticationComponent extends OnDestroyMixin implements OnInit {


  isLoading = false;
  isSaved = false;
  phone = '';

  base64Image = '';

  @Input()
  model: AgentDetailsResponse;

  formGroup: UntypedFormGroup;

  waitingForCode = false;

  get showPhoneField() {
    return this.formGroup.get('twoFactorEnabled').value &&
      this.model.isTwoFactorAuthenticationEnabled === false;
  }

  constructor(
    private userService: UserService,
    private snackbarService: SnackbarService,
    private dialogService: DialogService
  ) {
    super();
  }


  ngOnInit() {

    this.formGroup = new UntypedFormGroup({
      twoFactorEnabled: new UntypedFormControl(this.model.isTwoFactorAuthenticationEnabled, []),
      phone: new UntypedFormControl(this.model.phone1, [Validators.required]),
      verificationCode: new UntypedFormControl('', [Validators.maxLength(6)])
    });

    this.formGroup.get('twoFactorEnabled').valueChanges.pipe(distinctUntilChanged()).subscribe(newValue => {
      if (newValue === false) {

        const dialogParams = new ConfirmDialogParams();
        dialogParams.panelClass = 'disable-two-factor-dialog';
        dialogParams.actionLabel = `Disable`;
        dialogParams.title = `Are you sure you want to disable two factor authentication?`;
        dialogParams.description = ``;

        const dialogRef = this.dialogService.confirmDialog(dialogParams);
        dialogRef
          .afterClosed()
          .pipe(untilComponentDestroyed(this))
          .subscribe((confirmed: boolean) => {
            if (confirmed) {
              this.disableTwoFactor();
            } else {
              this.formGroup.get('twoFactorEnabled').setValue(true);
            }
          });
      }
    });
  }

  sendSmsVerification() {
    this.isSaved = false;
    this.isLoading = true;

    this.phone = this.formGroup.get('phone').value;

    this.userService
      .sendSmsVerification(new VerifyPhoneReqRes(this.phone))
      .pipe(
        finalize(() => {
          this.userService.refreshedClientDetails$.next(null);
          this.isLoading = false;
        })
      )
      .subscribe(
        response => {

          this.model.isPhone1Verified = false;
          this.waitingForCode = true;
          this.isSaved = true;
        },
        error => {
          this.isLoading = false;
        }
      );
  }

  smsVerify() {
    this.isSaved = false;
    this.isLoading = true;

    const phone = this.formGroup.get('phone').value;
    const verificationCode = this.formGroup.get('verificationCode').value;

    this.userService
      .smsVerify(new VerifyPhoneReqRes(phone, verificationCode))
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        response => {

          this.model.isPhone1Verified = true;

          this.enableTwoFactor();

          this.isSaved = true;
        },
        error => {

          if (error.error && error.error.message === 'code mismatch') {
            this.formGroup.get('verificationCode').setErrors({ code: true });
          } else if (error.error && error.error.message === 'code expired') {
            this.formGroup.get('verificationCode').setErrors({ expired: true });
          } else {
            this.formGroup.get('verificationCode').setErrors({ general: true });
          }
        }
      );
  }

  enableTwoFactor() {
    this.isSaved = false;
    this.isLoading = true;

    this.userService
      .twoFactorEnable()
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        response => {

          this.model.isTwoFactorAuthenticationEnabled = true;
          this.formGroup.reset();
          this.formGroup.get('twoFactorEnabled').setValue(true);

          this.snackbarService.showGeneralMessage('Two-factor authentication enabled!', 7);

          this.isSaved = true;
        },
        error => { }
      );
  }

  resendCode() {
    this.isSaved = false;
    this.isLoading = true;

    this.userService
      .reSendSmsVerification()
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        response => {

          this.snackbarService.showGeneralMessage('Code sent!');
          this.isSaved = true;
        },
        error => { }
      );
  }

  disableTwoFactor() {
    this.isSaved = false;
    this.isLoading = true;

    this.userService
      .twoFactorDisable()
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        response => {

          this.model.isTwoFactorAuthenticationEnabled = false;
          this.waitingForCode = false;
          this.formGroup.reset();

          this.snackbarService.showGeneralMessage('Two-factor authentication disabled!', 7);
          this.isSaved = true;
        },
        error => { }
      );
  }
}
